/**
 * moduleHost.js — domain → module map for the One App Suite.
 *
 * Architecture: modules run on app.<domain>; the bare domain serves a
 * static sales page (nginx). Bare domains are also mapped as fallback
 * for domains without a sales page yet.
 *
 * hustleplaza.au keeps its own dedicated logic in plazaHost.js.
 *
 * Preview override (test before DNS cutover):
 *   ?as=eats|plate|directory|shop|properties|press|meetups|deals|
 *      connect|campus|tackle        → simulate that module host
 *   ?as=normal                      → clear the override
 */
export const MODULES = [
  ["hustleeats.au", {
    key: "eats", label: "Eats", home: "/eat", title: "HustleEats",
    desc: "Order from local restaurants — menus, offers and table bookings.",
    color: "#f59e0b", initials: "HE",
  }],
  ["hustleplate.au", {
    key: "plate", label: "Plate", home: "/", title: "HustlePlate",
    desc: "Your restaurant's own ordering site — menus, pickup and delivery.",
    color: "#ef4444", initials: "HP",
  }],
  ["hustledirectory.au", {
    key: "directory", label: "Directory", home: "/", title: "HustleDirectory",
    subtitle: "Australia's largest ABN-verified directory",
    desc: "Discover trusted local businesses, services and venues across Australia.",
    color: "#b08a3e", initials: "HD", host: "https://hustledirectory.au",
  }],
  ["hustlestadium.au", {
    key: "stadium", label: "Stadium", home: "/", title: "HustleStadium",
    subtitle: "Match-day food ops for stadiums, arenas + festivals",
    desc: "Stall-menu QR ordering + group split-pay + live venue maps.",
    color: "#c2410c", initials: "HS", host: "https://hustlestadium.au",
  }],
  ["hustleshop.au", {
    key: "shop", label: "Shop", home: "/hustleshop", title: "HustleShop",
    desc: "Curated products and everyday essentials, delivered Australia-wide.",
    color: "#3b82f6", initials: "HS",
  }],
  ["hustleproperties.au", {
    key: "properties", label: "Properties", home: "/property", title: "HustleProperties",
    desc: "Real estate, rentals and property services in one place.",
    color: "#8b5cf6", initials: "HP",
  }],
  ["hustlepress.au", {
    key: "press", label: "Press", home: "/hustlepress", title: "HustlePress",
    desc: "Local news, deals and stories worth your inbox.",
    color: "#e11d48", initials: "HP",
  }],
  ["hustlemeetups.au", {
    key: "meetups", label: "Meetups", home: "/", title: "HustleMeetups",
    desc: "Find your people — groups, events and dinners near you.",
    color: "#06b6d4", initials: "HM",
  }],
  ["hustleroundup.au", {
    key: "roundup", label: "Roundup", home: "/", title: "HustleRoundup",
    desc: "Live video rooms with AI-generated recap emails — Zoom, without the friction.",
    color: "#f97316", initials: "HR",
  }],
  ["hustlebargains.au", {
    key: "deals", label: "Bargains", home: "/deals", title: "HustleBargains",
    desc: "Vouchers, coupons and local deals — hand-picked, ABN-verified, live-checked.",
    color: "#10b981", initials: "HB",
  }],
  ["gethustleconnect.com", {
    key: "connect", label: "Connect", home: "/connect", title: "HustleConnect",
    desc: "The WordPress plugin that wires your WP site into the Suite.",
    color: "#f97316", initials: "HC",
  }],
  ["hustlecampus.au", {
    key: "campus", label: "Campus", home: "/campus", title: "HustleCampus",
    desc: "Courses, lessons and certificates from the Suite's own campus.",
    color: "#eab308", initials: "HC",
  }],
  ["hustlechat.au", {
    key: "chat", label: "Chat", home: "/hustlechat", title: "HustleChat",
    desc: "Message venues, groups and your crew — chat across the Suite.",
    color: "#22d3ee", initials: "HC",
  }],
  ["hustlerewards.au", {
    key: "rewards", label: "Rewards", home: "/account/credits", title: "HustleRewards",
    desc: "Earn and spend rewards across every Suite module.",
    color: "#a3e635", initials: "HR",
  }],
  ["hustleappz.com", {
    key: "appz", label: "Appz", home: "/", title: "HustleAppz",
    desc: "Software development by the team behind the Suite.",
    color: "#818cf8", initials: "HA",
  }],
  ["hustlemail.au", {
    key: "mail", label: "Mail", home: "/", title: "HustleMail",
    desc: "The Suite's mail platform.",
    color: "#f472b6", initials: "HM",
  }],
  ["hustlerecipes.au", {
    key: "recipes", label: "Recipes", home: "/recipes", title: "HustleRecipes",
    desc: "The Suite's recipe collection — cook it, plan it, shop it.",
    color: "#fb923c", initials: "HR",
  }],
  ["hustlebnb.au", {
    key: "bnb", label: "BnB", home: "/stays", title: "HustleBnB",
    desc: "Your place, earning — host bookings, guests and payouts.",
    color: "#e879f9", initials: "HB",
  }],
  ["hustlestays.au", {
    key: "stays", label: "Stays", home: "/stays", title: "HustleStays",
    desc: "Guest stays, welcome books and passes — pre-Olympics ready.",
    color: "#34d399", initials: "HS",
  }],
  ["hustlediy.au", {
    key: "diy", label: "DIY", home: "/property", title: "HustleDIY",
    desc: "DIY house sales — sell your own home without the agent.",
    color: "#14b8a6", initials: "HD",
  }],
  ["hustlereviews.au", {
    key: "reviews", label: "Reviews", home: "/reviews", title: "HustleReviews",
    desc: "Honest business reviews from real Australian customers.",
    color: "#f43f5e", initials: "HR",
  }],
  ["hustleopinions.au", {
    key: "opinions", label: "Opinions", home: "/hustlepress", title: "HustleOpinions",
    desc: "Opinions on goods, products and the local issues that matter.",
    color: "#a855f7", initials: "HO",
  }],
  ["tackle.au", {
    key: "tackle", label: "Tackle", home: "/tackle", title: "Tackle.au",
    desc: "Fishing tackle, gear and local know-how.",
    color: "#0ea5e9", initials: "T",
  }],
  // iter248: Register the two module hosts that render the app UI
  // directly (no separate sales domain). This makes `getModuleHost()`
  // resolve on hustlefind.au and app.hustletravel.au, which in turn
  // triggers the `#FDFCF7` cream-strip module header in
  // PlatformHeader.jsx (the user's "DB page name token" strip).
  // NOTE: hustletravel.au bare is still served by TravelSalesPage via
  // RootRouter — it never renders PlatformHeader, so it's untouched.
  ["hustlefind.au", {
    key: "finder", label: "Finder", home: "/finder", title: "HustleFinder",
    desc: "Kids games, Sudoku, toilets, parks, playgrounds & POIs — free, no login.",
    color: "#10b981", initials: "HF",
  }],
  ["hustlefinder.au", {
    key: "finder", label: "Finder", home: "/finder", title: "HustleFinder",
    desc: "Kids games, Sudoku, toilets, parks, playgrounds & POIs — free, no login.",
    color: "#10b981", initials: "HF",
  }],
  ["hustletravel.au", {
    key: "travel", label: "Travel", home: "/", title: "HustleTravel",
    desc: "Hotels, flights and travel experiences.",
    color: "#38bdf8", initials: "HT",
  }],
  ["app.hustletravel.au", {
    key: "roadtrip", label: "Roadtrip", home: "/roadtrip", title: "HustleRoadtrip",
    desc: "Kids games, adult games, Sudoku & road-trip planning — iOS-first.",
    color: "#10b981", initials: "HR",
  }],
  // ─── HustleShield Forms ─────────────────────────────────────────
  // Two hosts, same brand:
  //   hustleshield.au       → sales page rendered inline at `/` by
  //                            RootRouter (mode "shield-sales"). No
  //                            /hustleshield.au URL subfolder.
  //   app.hustleshield.au   → the app itself (login → admin forms)
  //   emailspamshield.com   → USD sales mirror rendered inline at `/`
  //                            by RootRouter (mode "shield-us-sales").
  ["hustleshield.au", {
    key: "shield", label: "Shield", home: "/", title: "HustleShield",
    desc: "Drag-and-drop forms with an 8-layer silent-drop anti-spam shield baked in.",
    color: "#facc15", initials: "HS",
  }],
  ["app.hustleshield.au", {
    key: "shield-app", label: "Shield", home: "/admin/forms", title: "HustleShield Forms",
    desc: "Form builder, Shield dashboard and Agency console.",
    color: "#facc15", initials: "HS",
  }],
  ["emailspamshield.com", {
    key: "shield-us", label: "Shield", home: "/", title: "EmailSpamShield",
    desc: "Stop contact-form spam. For good. Without hurting conversions.",
    color: "#facc15", initials: "ES",
  }],
  ["hustleevents.au", {
    key: "events", label: "Events", home: "/hustle-events", title: "HustleEvents",
    desc: "Sell tickets, pick seats, and put every ticket in the buyer's wallet.",
    color: "#f97316", initials: "HE",
  }],
  ["hustletickets.au", {
    key: "tickets", label: "Tickets", home: "/tickets", title: "HustleTickets",
    desc: "Zero-fee ticketing for regional theatres — SVG seat-maps, wallet passes.",
    color: "#f59e0b", initials: "HT",
  }],
  ["hustlecircles.au", {
    key: "circles", label: "Circles", home: "/circles", title: "HustleCircles",
    desc: "Private community circles — invite-only, moderator-run.",
    color: "#a855f7", initials: "HC",
  }],
  ["hustlematchmaker.au", {
    key: "matchmaker", label: "Matchmaker", home: "/matchmaker", title: "HustleMatchmaker",
    desc: "Real-world introductions — human matchmakers, no swiping.",
    color: "#ec4899", initials: "HM",
  }],
  ["hustleanswer.au", {
    key: "answer", label: "Answer", home: "/hustleanswer", title: "HustleAnswer",
    desc: "The Hustle Ai Receptionist — answers every call, second ring, in 12 languages.",
    color: "#0891b2", initials: "HA",
  }],
];

const MODULE_HOSTS = {};
MODULES.forEach(([domain, mod]) => {
  MODULE_HOSTS[domain] = mod;
  MODULE_HOSTS[`app.${domain}`] = mod;
});
// Domain aliases (e.g. .au twin of a .com module domain).
const HOST_ALIASES = {
  // SeatingMaker: seatmaps.au is the shorthand alias for
  //   hustleseatmaps.au (both point at the same brand).
  "seatmaps.au":      "hustleseatmaps.au",
};
Object.entries(HOST_ALIASES).forEach(([alias, target]) => {
  MODULE_HOSTS[alias] = MODULE_HOSTS[target];
  MODULE_HOSTS[`app.${alias}`] = MODULE_HOSTS[target];
});

// Suite-bar links, in display order. Plaza included (external logic).
export const SUITE_LINKS = [
  { key: "eats",       label: "Eats",       home: "/eat" },
  { key: "plaza",      label: "Plaza",      home: "/plaza/shop" },
  { key: "shop",       label: "Shop",       home: "/hustleshop" },
  { key: "directory",  label: "Directory",  home: "/directory" },
  { key: "properties", label: "Properties", home: "/property" },
  { key: "meetups",    label: "Meetups",    home: "/meetup" },
  { key: "tickets",    label: "Tickets",    home: "/tickets" },
  { key: "events",     label: "Events",     home: "/hustle-events" },
  { key: "circles",    label: "Circles",    home: "/circles" },
  { key: "matchmaker", label: "Matchmaker", home: "/matchmaker" },
  { key: "deals",      label: "Bargains",   home: "/deals" },
  { key: "press",      label: "Press",      home: "/hustlepress" },
  { key: "campus",     label: "Campus",     home: "/campus" },
  { key: "connect",    label: "Connect",    home: "/connect" },
  { key: "tackle",     label: "Tackle",     home: "/tackle" },
  { key: "moments",    label: "Moments",    home: "/moments" },
];

const OVERRIDE_KEY = "hs-module-host-override";
const VALID_KEYS = new Set(MODULES.map(([, m]) => m.key));

function readOverride() {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const q = url.searchParams.get("as");
    if (q && (VALID_KEYS.has(q) || q === "normal")) {
      if (q === "normal") {
        sessionStorage.removeItem(OVERRIDE_KEY);
        sessionStorage.removeItem("hpz-host-override");
      } else sessionStorage.setItem(OVERRIDE_KEY, q);
      url.searchParams.delete("as");
      window.history.replaceState({}, "", url.toString());
      return q === "normal" ? null : q;
    }
    return sessionStorage.getItem(OVERRIDE_KEY);
  } catch { return null; }
}

/** Returns the module descriptor for the current host, or null. */
export function getModuleHost() {
  if (typeof window === "undefined") return null;
  const override = readOverride();
  if (override && VALID_KEYS.has(override)) {
    return MODULES.find(([, m]) => m.key === override)?.[1] || null;
  }
  // Strip `demo.` prefix so demo.<host> resolves to the same module
  // descriptor as <host>. DemoModeBanner detects the raw hostname and
  // overlays the demo-login pill separately.
  const host = (window.location.hostname || "")
    .toLowerCase()
    .replace(/^www\./, "")
    .replace(/^demo\./, "");
  return MODULE_HOSTS[host] || null;
}

/**
 * Brand descriptor for the current host — module hosts PLUS the two
 * umbrella hosts (hustlesuite / hustleplaza) that own their branding but
 * live outside MODULES. Single source of truth for tab title, favicon,
 * login copy and manifest. Returns null only on tenant/custom domains.
 */
export function getBrandHost() {
  const mod = getModuleHost();
  if (mod) return mod;
  if (typeof window === "undefined") return null;
  const host = (window.location.hostname || "").toLowerCase();
  if (host.includes("hustlesuite")) {
    return { key: "suite", title: "HustleSuite",
             desc: "One account for every module of the Suite.",
             color: "#10b981", initials: "HS" };
  }
  if (host.includes("hustleplaza") || host === "plaza.au" || host === "www.plaza.au") {
    return { key: "plaza", title: "HustlePlaza",
             desc: "The Australian marketplace — one cart, one wallet.",
             color: "#8b5cf6", initials: "HP" };
  }
  if (host.includes("hustlefind")) {
    return { key: "finder", title: "HustleFinder",
             desc: "Find toilets, drinking fountains, parks, bus stops and more — Australia-wide.",
             color: "#10b981", initials: "HF" };
  }
  if (host.includes("hustletravel")) {
    return { key: "travel", title: "HustleTravel",
             desc: "Hotels, flights, experiences and road-trip planning.",
             color: "#38bdf8", initials: "HT" };
  }
  if (host.includes("hustleevents")) {
    return { key: "events", title: "HustleEvents",
             desc: "Sell tickets, pick seats, and put every ticket in the buyer's wallet.",
             color: "#0f172a", initials: "HE" };
  }
  if (host.includes("hustledictate")) {
    return { key: "dictate", title: "HustleDictate",
             desc: "Speech-to-text on any form, on any website.",
             color: "#e879f9", initials: "HD" };
  }
  if (host.includes("hustleseatmaps") || host.includes("seatmaps")) {
    return { key: "seatingmaker", title: "SeatingMaker",
             desc: "Canva, but for seat maps. Draw any venue in an afternoon.",
             color: "#10b981", initials: "SM" };
  }
  return null;
}

/** Where the "Suite" link in the global bar should point. */
export function suiteHref() {
  if (typeof window === "undefined") return "/";
  const host = (window.location.hostname || "")
    .toLowerCase()
    .replace(/^www\./, "");
  if (MODULE_HOSTS[host]) return "https://app.hustlesuite.au";
  return "/";
}

/**
 * Post-login destination. Honours ?next=, then routes agency owners to
 * the module home on module hosts (app.hustlechat.au → /hustlechat) and
 * to the Suite Console on hustlesuite hosts. Regular members on the
 * umbrella host (hustlesuite.au) land on the PWA launcher.
 */
export function safeLoginDestination(value) {
  if(typeof value!=="string" || !value.startsWith("/") || value.startsWith("//") || /[\\\r\n]/.test(value)) return null;
  if(/^\/(?:admin\/)?login(?:[/?#]|$)/.test(value)) return null;
  return value;
}

export function postLoginPath(role) {
  if (typeof window !== "undefined") {
    try {
      const next = new URLSearchParams(window.location.search).get("next");
      const safeNext=safeLoginDestination(next);
      if(safeNext)return safeNext;
    } catch { /* ignore */ }
    const host = (window.location.hostname || "").toLowerCase();
    if (role === "agency_owner") {
      if (host.includes("hustlesuite")) return "/agency/settings";
      const mod = getModuleHost();
      if (mod) return mod.home;
      return "/agency";
    }
    // Regular members on the umbrella host (hustlesuite.au) → PWA
    // launcher. Members already on a module host stay on that module.
    const onModule = !!getModuleHost();
    const onSuiteUmbrella = host.endsWith("hustlesuite.au")
                            && !host.startsWith("pwa.")
                            && !host.startsWith("admin.");
    if (!onModule && onSuiteUmbrella) {
      return "https://pwa.hustlesuite.au/";
    }
  }
  if (role === "agency_owner") return "/agency";
  if (role === "admin" || role === "business_admin") return "/admin";
  return "/account";
}

// Cross an isolated application boundary with a real navigation so its router
// mounts with the correct base. Ordinary in-app destinations keep SPA navigation.
export function goPostLogin(navigate, dest, browserWindow=window) {
  if (typeof dest === "string" && (/^https?:\/\//i.test(dest) ||
      dest.startsWith("/api/hc-shell/unified-preview/"))) {
    browserWindow.location.assign(dest);
    return;
  }
  navigate(dest, { replace: true });
}
